import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { FormBuilder, FormGroup } from '@angular/forms';
import { JobFunctions } from 'src/app/shared/dw-column';

@Component({
    selector: 'app-job-function-dropdown',
    templateUrl: './job-function-dropdown.component.html',
    styleUrls: ['./job-function-dropdown.component.scss'],
    standalone: false
})
export class JobFunctionDropdownComponent implements OnChanges, OnInit {
  @Input() preSelectedJobFunctions: string[] | undefined = undefined;
  @Input() multiSelect: boolean = false;
  @Input() useChipDisplay: boolean = false;
  @Output() jobFunctionSelectionChanged = new EventEmitter<string[]>();
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  availableJobFunctions: string[] = this.jobFunctionsAsArray();
  form: FormGroup;
  private lastEmittedValue: string[] = [];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      selectedFunctions: [this.preSelectedJobFunctions || this.availableJobFunctions],
    });
    this.lastEmittedValue = this.form.get('selectedFunctions')?.value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.preSelectedJobFunctions && !changes.preSelectedJobFunctions.firstChange) {
      const newPreSelectedJobFunctions = changes.preSelectedJobFunctions.currentValue;

      if (newPreSelectedJobFunctions) {
        this.form
          .get('selectedFunctions')
          ?.setValue(newPreSelectedJobFunctions, { emitEvent: false });
        this.lastEmittedValue = this.form.get('selectedFunctions')?.value;
      }
    }
  }

  // When the menu is closed, emit the value if it has changed
  onSelectionChange(value: any) {
    if (this.hasSelectionChanged(this.lastEmittedValue, value)) {
      this.jobFunctionSelectionChanged.emit(value);
      this.lastEmittedValue = value;
    }
  }

  // Triggered when the dropdown panel is closed instead of single checkbox selection event
  onDropdownClosed() {
    // In form field mode, convert empty to "all selected" for better UX
    if (
      this.form.get('selectedFunctions')?.value === undefined ||
      this.form.get('selectedFunctions')?.value.length === 0
    ) {
      this.form.get('selectedFunctions')?.setValue(this.availableJobFunctions);
    }

    this.onSelectionChange(this.form.get('selectedFunctions')?.value);
  }

  onMenuClosed() {
    // In chip mode, allow empty arrays (means "no filter")
    // In form field mode, convert empty to "all selected" for better UX
    if (
      !this.useChipDisplay &&
      (this.form.get('selectedFunctions')?.value === undefined ||
        this.form.get('selectedFunctions')?.value.length === 0)
    ) {
      this.form.get('selectedFunctions')?.setValue(this.availableJobFunctions);
    }

    this.onSelectionChange(this.form.get('selectedFunctions')?.value);
  }

  selectAll() {
    this.form.get('selectedFunctions')?.setValue(this.availableJobFunctions);
  }

  unselectAll() {
    const emptyValue: string[] = [];
    this.form.get('selectedFunctions')?.setValue(emptyValue);
    // this.onSelectionChange(emptyValue);
  }

  clearSelection() {
    // Clear the filter by setting to empty array (show all, no filter)
    const emptyValue: string[] = [];
    this.form.get('selectedFunctions')?.setValue(emptyValue);
    this.onSelectionChange(emptyValue);
  }

  areAllSelected() {
    return this.form.get('selectedFunctions')?.value?.length === this.availableJobFunctions.length;
  }

  areNoneSelected() {
    return (
      this.form.get('selectedFunctions')?.value === undefined ||
      this.form.get('selectedFunctions')?.value.length === 0
    );
  }

  applyAndClose() {
    this.onSelectionChange(this.form.get('selectedFunctions')?.value);
    this.menuTrigger?.closeMenu();
  }

  getCollapsedStringValue(): string {
    if (this.form.get('selectedFunctions')?.value === undefined) {
      return 'All';
    }

    if (this.form.get('selectedFunctions')?.value.length === 0) {
      return 'All';
    }

    if (this.form.get('selectedFunctions')?.value.length === this.availableJobFunctions.length) {
      return 'All';
    }

    return this.form.get('selectedFunctions')?.value.join(', ');
  }

  get isFilterActive(): boolean {
    const selectedFunctions = this.form.get('selectedFunctions')?.value;
    return !!(
      selectedFunctions &&
      selectedFunctions.length > 0 &&
      selectedFunctions.length < this.availableJobFunctions.length
    );
  }

  get chipText(): string {
    const selectedFunctions = this.form.get('selectedFunctions')?.value;
    if (!selectedFunctions || selectedFunctions.length === 0) {
      return '';
    }

    const count = selectedFunctions.length;
    const firstFunction = selectedFunctions[0];

    if (count === 1) {
      return firstFunction;
    }

    return `${firstFunction} + ${count - 1} more`;
  }

  // Helper function to compare arrays (lastEmitted vs current selection)
  private hasSelectionChanged(lastEmitted: string[], current: string[]): boolean {
    if (lastEmitted.length !== current.length) {
      return true;
    }

    return lastEmitted.some((item) => !current.includes(item));
  }

  jobFunctionsAsArray(): string[] {
    return Object.values(JobFunctions);
  }
}
